/**
 * Browser storage, read and written without throwing: storage can be off
 * (private mode, blocked site data) or full, and nothing in Sorrem may break
 * because of it. Every key Sorrem keeps is listed here, so two features never
 * share one by accident.
 */

/** This tab's session token (session storage). */
export const SESSION_TOKEN_KEY = 'sorrem:session-token';
/** This tab's signed-in account id, stored canonical (session storage). */
export const ACCOUNT_ID_KEY = 'sorrem:account-id';
/** The gathering this tab joined, for the run page to pick up (session storage). */
export const CRUCIBLE_JOIN_SESSION_KEY = 'sorrem:crucible-join-session';
/** Set once Character Home has been entered since this login (session storage). */
export const HAS_ENTERED_HOME_KEY = 'sorrem:has-entered-home';
/** This tab's selected character (session storage). */
export const SELECTED_CHARACTER_ID_KEY = 'sorrem:selected-character-id';
/** Prefix of each account's default character, `<prefix>:<accountId>` (local storage). */
export const DEFAULT_CHARACTER_ID_KEY = 'sorrem:default-character-id';
/** Where a signed-out player was headed, for login to return to (session storage). */
export const POST_LOGIN_PATH_KEY = 'sorrem:post-login-path';
/** The run's debug overlay toggles (local storage). */
export const DEBUG_OVERLAY_SETTINGS_KEY = 'sorrem:debug-overlay-settings';

/** Which storage a value lives in: this tab only, or every tab and later visits. */
export type BrowserStorageArea = 'session' | 'local';

function getStorage(area: BrowserStorageArea): Storage {
    return area === 'session' ? window.sessionStorage : window.localStorage;
}

/** A stored value, or `undefined` when it is missing, blank, or storage is unavailable. */
export function getStoredValue(area: BrowserStorageArea, key: string): string | undefined {
    try {
        const value = getStorage(area).getItem(key);
        return value !== null && value.trim().length > 0 ? value : undefined;
    } catch {
        return undefined;
    }
}

/** Stores a value; without storage it is simply not kept. */
export function storeValue(area: BrowserStorageArea, key: string, value: string): void {
    try {
        getStorage(area).setItem(key, value);
    } catch {
        // Private mode or a full quota: the value just isn't remembered.
    }
}

/** Removes a stored value; without storage there is nothing to remove. */
export function forgetStoredValue(area: BrowserStorageArea, key: string): void {
    try {
        getStorage(area).removeItem(key);
    } catch {
        // Nothing stored to forget.
    }
}
